import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone, User } from 'lucide-react';
import { BRAND, NAV } from '@/data/content';
import { useAuth } from '@/auth/AuthContext';

// Split full nav (including Request an Estimate as a nav item) around the centered logo
// Left of logo: Home, Services, Gallery, About  |  Right of logo: Service Areas, Blog, Request an Estimate
const FULL_NAV = [...NAV, { label: 'Request an Estimate', to: '/estimate', cta: true }];
const LEFT_NAV = FULL_NAV.slice(0, 4);
const RIGHT_NAV = FULL_NAV.slice(4);

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { user } = useAuth();
  const isHome = pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // On home page, header floats over the hero image. On other pages, always solid.
  const isFloating = isHome && !scrolled && !open;

  const navLinkClass = ({ isActive }) =>
    `font-sans font-bold text-[11px] xl:text-[12px] uppercase tracking-[0.16em] transition-colors whitespace-nowrap ${
      isActive ? 'text-brand-bone' : 'text-brand-bone/85 hover:text-brand-bone'
    }`;

  return (
    <header
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        isFloating
          ? 'bg-transparent border-b border-transparent'
          : 'surface-dark border-b border-brand-borderDark shadow-[0_1px_0_0_rgba(0,0,0,0.4)]'
      }`}
      data-testid="site-header"
    >
      {/* Top strip */}
      <div className={`hidden md:block border-b ${isFloating ? 'border-transparent' : 'border-brand-borderDark'}`}>
        <div className="container-x flex items-center justify-between py-1.5 text-[11px] font-sans text-brand-bone/60">
          <span className="tracking-wide uppercase">BBB Accredited · Owner-led · Fort Worth · Burleson · DFW</span>
          <div className="flex items-center gap-5">
            <Link
              to={user ? (user.role === 'admin' ? '/admin' : '/account') : '/login'}
              className="flex items-center gap-1.5 hover:text-brand-bone transition-colors"
              data-testid="top-account-link"
            >
              <User size={12} strokeWidth={2} /> {user ? 'Account' : 'Sign in'}
            </Link>
            <a href={BRAND.phoneHref} className="flex items-center gap-1.5 hover:text-brand-bone transition-colors" data-testid="top-phone-link">
              <Phone size={12} strokeWidth={2} /> {BRAND.phoneDisplay}
            </a>
            <a href={BRAND.emailHref} className="hover:text-brand-bone transition-colors" data-testid="top-email-link">
              {BRAND.email}
            </a>
          </div>
        </div>
      </div>

      {/* Main bar — sitemap layout: [nav-left] [centered logo] [nav-right] */}
      <div className="container-x">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 md:gap-8 lg:gap-10 py-3 md:py-3 lg:py-4">
          {/* LEFT */}
          <div className="flex items-center justify-start md:justify-end">
            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 -ml-2 text-brand-bone"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
            {/* Tablet/desktop left nav */}
            <nav className="hidden md:flex items-center gap-4 lg:gap-6 xl:gap-8" data-testid="desktop-nav-left">
              {LEFT_NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === '/'}
                  className={(state) => navLinkClass(state)}
                  data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>

          {/* CENTER: logo */}
          <div className="flex justify-center">
            <Link to="/" className="flex items-center justify-center" data-testid="header-logo-link">
              <img
                src={BRAND.logoCircle}
                alt="HearthOak Construction logo"
                className="h-12 w-12 md:h-14 md:w-14 lg:h-20 lg:w-20 xl:h-24 xl:w-24 object-contain"
              />
            </Link>
          </div>

          {/* RIGHT */}
          <div className="flex items-center justify-end md:justify-start gap-3 md:gap-4 lg:gap-6">
            <nav className="hidden md:flex items-center gap-4 lg:gap-6 xl:gap-8" data-testid="desktop-nav-right">
              {RIGHT_NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className={(state) => navLinkClass(state, n.cta)}
                  data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
            {/* Mobile phone-call shortcut */}
            <a
              href={BRAND.phoneHref}
              className="md:hidden p-2 -mr-2 text-brand-bone"
              aria-label="Call us"
              data-testid="mobile-phone-icon"
            >
              <Phone size={20} strokeWidth={2} />
            </a>
          </div>
        </div>
      </div>

      {/* Mobile nav drawer — only used on phones (< md) */}
      {open && (
        <div className="md:hidden surface-dark border-t border-brand-borderDark" data-testid="mobile-nav">
          <nav className="container-x py-5 flex flex-col gap-1">
            {FULL_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-2.5 font-sans text-sm uppercase tracking-[0.18em] border-b border-brand-borderDark ${
                    isActive ? 'text-brand-maroonBright' : n.cta ? 'text-brand-maroonBright' : 'text-brand-bone/85'
                  }`
                }
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {n.label}
              </NavLink>
            ))}
            <a href={BRAND.phoneHref} className="mt-3 py-2 text-brand-bone/85 font-sans text-sm flex items-center gap-2">
              <Phone size={14} /> {BRAND.phoneDisplay}
            </a>
            <Link
              to={user ? (user.role === 'admin' ? '/admin' : '/account') : '/login'}
              onClick={() => setOpen(false)}
              className="py-2 text-brand-bone/85 font-sans text-sm flex items-center gap-2"
              data-testid="mobile-account-link"
            >
              <User size={14} /> {user ? 'My account' : 'Sign in / Create account'}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
